import { Div } from '@moniebook-web/components/atoms/animation';
import { Button, LinkButton } from '@moniebook-web/components/atoms/button';
import { SelectSearchContainer } from '@moniebook-web/components/atoms/form';
import { Modal } from '@moniebook-web/components/atoms/modal';
import { ProgressBar } from '@moniebook-web/components/atoms/progress-bar';
import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { AddLine } from '@moniebook-web/icons/system/add-line';
import { CloseFill } from '@moniebook-web/icons/system/close-fill';

import { useSessionTokenPayload } from '@/features/ghost-session/context/session-token-payload-context';
import { BUSINESS_PAGES, FEATURE_FLAGS } from '@/shared/constants';

import { useBusinessSwitcher } from './hooks/use-business-switcher';
import { BusinessList } from './partials/business-list';
import { SwitchContentSkeleton } from './partials/switch-content-skeleton';
import type { BusinessSwitcherProps } from './types';

export function BusinessSwitcher({
    open,
    onClose,
}: Readonly<BusinessSwitcherProps>) {
    const { isGhostSession } = useSessionTokenPayload();
    const {
        search,
        searchRef,
        searchResults,
        businessData,
        newBusinessName,
        currentBName,
        switching,
        progress,
        oldName,
        handleOnSelect,
        handleSearch,
        canCreateBusiness,
        isBusinessListLoading,
        isMobile,
        handleCreateBusiness,
    } = useBusinessSwitcher({ onClose });

    const showSwitchingUI = switching && !isBusinessListLoading;
    const showContent =
        !isBusinessListLoading &&
        !switching &&
        newBusinessName !== currentBName;

    if (isGhostSession) {
        return null;
    }

    return (
        <Modal
            closeOnEscape={!switching}
            position={isMobile ? 'bottom' : 'center'}
            isOpen={open}
            onClose={onClose}
            removeBodyPadding>
            {open && (
                <div className='p-8'>
                    <div className='flex items-center justify-between px-16 py-8'>
                        <h2 className='font-general-sans font-semibold text-18 text-neutral-s14'>
                            {switching
                                ? 'Switching business'
                                : 'Switch Business'}
                        </h2>
                        <Button
                            onClick={switching ? undefined : onClose}
                            aria-label='Close'
                            disabled={switching}
                            icon={
                                <CloseFill className='size-16 text-new-neutral-700' />
                            }
                            theme='tertiary'
                        />
                    </div>

                    {showSwitchingUI && (
                        <Div className='mx-16 mt-4 mb-20 flex flex-col gap-24'>
                            <p className='font-normal text-14 text-neutral-s10'>
                                Switching from {oldName} to {newBusinessName}
                            </p>
                            <div className='flex-1'>
                                <ProgressBar percentage={progress} />
                            </div>
                        </Div>
                    )}

                    {isBusinessListLoading && <SwitchContentSkeleton />}

                    {showContent && (
                        <>
                            <div className='mx-8 mb-13 border-y border-y-headless-stroke'>
                                <SelectSearchContainer
                                    onSearch={handleSearch}
                                    search={search}
                                    searchRef={searchRef}
                                    searchPlaceholder='Search business by name'
                                    showBottomBorder={false}
                                />
                            </div>
                            <BusinessList
                                businesses={
                                    search ? searchResults : businessData
                                }
                                onSelectCurrentBusiness={handleOnSelect}
                                search={search}
                            />
                            <FeatureProtectedView
                                flag={
                                    FEATURE_FLAGS.ENABLE_NEW_BUSINESS_CREATION
                                }
                                noAltView>
                                {canCreateBusiness && (
                                    <LinkButton
                                        label='Create new business'
                                        to={BUSINESS_PAGES.BUSINESS_DETAILS}
                                        className='w-full md:w-auto'
                                        leftIcon={<AddLine />}
                                        onClick={handleCreateBusiness}
                                    />
                                )}
                            </FeatureProtectedView>
                        </>
                    )}
                </div>
            )}
        </Modal>
    );
}
